import { Link } from "react-router-dom";
import { Instagram, Facebook, Phone, Mail, MapPin } from "lucide-react";
import { Logo } from "@/components/Logo";
import { CATEGORIES } from "@/data/content";
import { useConfig } from "@/context/ConfigContext";
import { waLink, telLink } from "@/config/site";
import { WhatsAppIcon } from "@/components/WhatsAppFloat";

const quickLinks = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Categories", to: "/categories" },
  { label: "Brands", to: "/brands" },
  { label: "Solutions", to: "/solutions" },
  { label: "Contact", to: "/contact" },
];

export function Footer() {
  const cfg = useConfig();
  return (
    <footer className="bg-darknavy text-cream" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Logo dark />
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-cream/60">
              Distributor, wholesaler and retailer of building materials, paints, plumbing, bathware and home solutions — trusted brands under one roof.
            </p>
            <div className="mt-6 flex gap-3">
              <a href={cfg.instagram} aria-label="Instagram" data-testid="footer-instagram" className="flex h-10 w-10 items-center justify-center border border-cream/20 text-cream/70 transition-colors hover:border-gold hover:text-gold">
                <Instagram className="h-4 w-4" />
              </a>
              <a href={cfg.facebook} aria-label="Facebook" data-testid="footer-facebook" className="flex h-10 w-10 items-center justify-center border border-cream/20 text-cream/70 transition-colors hover:border-gold hover:text-gold">
                <Facebook className="h-4 w-4" />
              </a>
            </div>
          </div>
          <div>
            <h3 className="font-display text-xs font-semibold uppercase tracking-[0.3em] text-gold">Quick Links</h3>
            <ul className="mt-5 space-y-3">
              {quickLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-cream/70 transition-colors hover:text-cream" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-display text-xs font-semibold uppercase tracking-[0.3em] text-gold">Categories</h3>
            <ul className="mt-5 space-y-3">
              {CATEGORIES.slice(0, 6).map((c) => (
                <li key={c.slug}>
                  <Link to={`/categories/${c.slug}`} className="text-sm text-cream/70 transition-colors hover:text-cream" data-testid={`footer-cat-${c.slug}`}>
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-display text-xs font-semibold uppercase tracking-[0.3em] text-gold">Contact</h3>
            <ul className="mt-5 space-y-4 text-sm text-cream/70">
              <li className="flex items-start gap-3">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <a href={telLink(cfg)} className="hover:text-cream transition-colors" data-testid="footer-phone">{cfg.phone}</a>
              </li>
              <li className="flex items-start gap-3">
                <WhatsAppIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <a href={waLink(cfg)} target="_blank" rel="noopener noreferrer" className="hover:text-cream transition-colors" data-testid="footer-whatsapp">Chat on WhatsApp</a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <a href={`mailto:${cfg.email}`} className="hover:text-cream transition-colors" data-testid="footer-email">{cfg.email}</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <span data-testid="footer-address">{cfg.address}</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-cream/10 pt-8 md:flex-row">
          <p className="text-xs tracking-wide text-cream/50">© Chhannulal &amp; Brothers. All rights reserved.</p>
          <p className="font-display text-[11px] uppercase tracking-[0.3em] text-cream/40">CLB · Your Complete Home Solutions</p>
        </div>
      </div>
    </footer>
  );
}
